import React, { useState, useEffect } from 'react'
import { pageCache } from '../utils/pageCache'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import TextListBottomSheet from '../components/TextListBottomSheet'
import { SavedPlaceCategory } from '../api/user'
import { bookmarkApi } from '../api/bookmark'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'
import { useAuthStore } from '../stores/authStore'
import { useCategoryStore } from '../stores/categoryIconStore'
import Gap from '../components/Gap'

/** 리스트 이름 길이 상한. 서버 검증(max:20)과 같은 값이다. */
const NAME_MAX = 20
const COPY_SUFFIX = '_복사본'

/**
 * 복사본 이름을 짓는다. "성수" → "성수_복사본", 또 복사하면 "성수_복사본2".
 *
 * 20자를 넘길 수 없다(서버가 거절한다). 그래서 **꼬리표가 들어갈 자리를 먼저
 * 빼고 원본 이름을 거기에 맞춰 자른다.** 꼬리표를 자르면 복사본인지 알 수 없으니
 * 자르는 쪽은 언제나 원본 이름이다.
 *
 *   1번째  "_복사본"(4자)  → 원본 16자까지
 *   2번째  "_복사본2"(5자) → 원본 15자까지
 *   10번째 "_복사본10"(6자)→ 원본 14자까지
 *
 * 이미 있는 이름은 피한다 — 같은 리스트를 두 번 복사해도 목록에서 구분된다.
 */
const buildCopyName = (base: string, taken: Set<string>): string => {
  const make = (n: number): string => {
    const suffix = n === 1 ? COPY_SUFFIX : `${COPY_SUFFIX}${n}`
    return base.slice(0, NAME_MAX - suffix.length) + suffix
  }
  for (let n = 1; n <= 99; n += 1) {
    const candidate = make(n)
    if (!taken.has(candidate)) return candidate
  }
  return make(1)
}

const PlateCategory: React.FC = () => {
  const navigate = useNavigate()
  const { userId, categoryId } = useParams<{
    userId: string
    categoryId: string
  }>()
  const { userId: currentUserId } = useAuthStore()
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown
  )
  const getCategoryName = useCategoryStore((state) => state.getCategoryName)
  const getAllCategories = useCategoryStore((state) => state.getAllCategories)
  const location = useLocation()
  // 진입 시 넘어온 플레이트 이름으로 초기화 → 로딩 중에도 올바른 제목 표시
  const initialCategoryName =
    (location.state as { categoryName?: string } | null)?.categoryName ?? ''
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `plate-category:${userId ?? ''}:${categoryId ?? ''}`
  const cached = pageCache.get<{
    categories: SavedPlaceCategory[]
    name: string
    ownerId: string | null
    thumbnail: string | null
    isMember: boolean
  }>(cacheKey)

  const [categories, setCategories] = useState<SavedPlaceCategory[]>(
    cached?.categories ?? []
  )
  const [categoryName, setCategoryName] = useState<string>(
    cached?.name ?? initialCategoryName
  )
  const [categoryOwnerId, setCategoryOwnerId] = useState<string | null>(
    cached?.ownerId ?? null
  )
  const [categoryThumbnail, setCategoryThumbnail] = useState<string | null>(
    cached?.thumbnail ?? null
  )
  const [isMember, setIsMember] = useState(cached?.isMember ?? false)
  const [isLoading, setIsLoading] = useState(!cached)
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [isPrivate, setIsPrivate] = useState(false)

  const isMine =
    currentUserId && categoryOwnerId && currentUserId === categoryOwnerId
  /*
   * 메뉴는 남의 리스트에서도 띄운다 — 거기서는 '복사'만 보인다.
   * 비공개라 내용을 못 보는 화면에서는 복사할 것도 없으므로 숨긴다.
   */
  const canShowMenu = !isPrivate && !isLoading
  const [isCopying, setIsCopying] = useState(false)

  useEffect(() => {
    if (!userId) {
      toastUtil.error('사용자 정보를 불러오는데 실패했습니다')
      navigate(-1)
      return
    }

    if (!categoryId) {
      toastUtil.error('카테고리 정보를 불러오는데 실패했습니다')
      navigate(-1)
      return
    }

    const loadData = async () => {
      // 캐시로 이미 그려져 있으면 로딩 화면으로 덮지 않는다
      if (!pageCache.has(cacheKey)) {
        setIsLoading(true)
      }
      setIsPrivate(false)
      try {
        // 북마크 카테고리 정보 가져오기
        const bookmarkResponse =
          await bookmarkApi.getBookmarksByCategory(categoryId)
        if (bookmarkResponse.categories.length > 0) {
          const category = bookmarkResponse.categories[0]
          setCategoryName(category.name)
          setCategoryOwnerId(category.user_id || null)
          setCategoryThumbnail(category.thumbnail_image ?? null)
          setIsMember(category.is_member ?? false)
          const places = bookmarkResponse.categories[0].items

          // 각 장소의 PlaceCategory 확인
          const categoryCounts = new Map<string, number>()

          // 초기화
          getAllCategories().forEach((cat) => {
            categoryCounts.set(cat.code, 0)
          })

          // 백엔드가 내려준 category로 바로 집계 (장소별 getPlace 호출 제거 → 빠름)
          places.forEach((place) => {
            const placeCategory = (place.category as string) || 'restaurant'
            categoryCounts.set(
              placeCategory,
              (categoryCounts.get(placeCategory) || 0) + 1
            )
          })

          // SavedPlaceCategory 형식으로 변환
          const categoryList: SavedPlaceCategory[] = getAllCategories().map(
            (cat) => ({
              category: cat.code,
              name: cat.name,
              count: categoryCounts.get(cat.code) || 0,
            })
          )

          setCategories(categoryList)
          pageCache.set(cacheKey, {
            categories: categoryList,
            name: category.name,
            ownerId: category.user_id || null,
            thumbnail: category.thumbnail_image ?? null,
            isMember: category.is_member ?? false,
          })
        }
      } catch (error: any) {
        // 403 에러인 경우 비공개 플레이트리스트로 처리
        if (error.response?.status === 403) {
          setIsPrivate(true)
          setCategories([])
        } else {
          const errorMessage =
            error.response?.data?.message ||
            '플레이트 카테고리를 불러오는데 실패했습니다'
          toastUtil.error(errorMessage)
          navigate(-1)
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, categoryId, navigate])

  const handleCategoryClick = (category: string) => {
    navigate(`/user/${userId}/plate-category/${categoryId}/${category}`)
  }

  const handleMeatballClick = () => {
    setIsBottomSheetOpen(true)
  }

  /**
   * 남의 플레이트리스트를 내 프로필로 복사한다.
   *
   * 새 API 가 필요 없다 — 리스트 만들기(createBookmarkCategory)가 이미
   * place_ids 를 받는다(링크 가져오기용으로 있던 것). 그래서 **장소 목록을
   * 다시 읽어 그 id 들로 새 리스트를 만드는** 두 번의 호출이면 된다.
   *
   * 따라오지 않는 것: 커버 이미지(안 보낸다), 멤버, 각 장소를 누가 담았는지.
   * 공개 여부만 원본을 따른다.
   */
  const handleCopyPlateList = async () => {
    if (!categoryId || isCopying) return
    setIsBottomSheetOpen(false)
    setIsCopying(true)
    try {
      // 화면에는 분류별 개수만 있고 장소 id 는 없다. 복사할 때 다시 읽는다.
      const res = await bookmarkApi.getBookmarksByCategory(categoryId)
      const source = res.categories[0]
      if (!source) {
        toastUtil.error('플레이트리스트를 불러오지 못했습니다')
        return
      }

      const placeIds = source.items
        .map((item) => parseInt(item.id, 10))
        .filter((id) => Number.isFinite(id))
      if (placeIds.length === 0) {
        toastUtil.error('복사할 장소가 없습니다')
        return
      }

      // 내 리스트 이름들을 먼저 받아 겹치지 않는 이름을 고른다
      const mine = await bookmarkApi.getBookmarkCategories()
      const taken = new Set(mine.categories.map((c) => c.name))

      const created = await bookmarkApi.createBookmarkCategory({
        name: buildCopyName(source.name, taken),
        placeIds,
        isPublic: source.is_public ?? true,
      })

      toastUtil.success('내 프로필에 복사했습니다')
      // 복사본 상세가 아니라 **내 프로필**로 보낸다. 목록에서 새로 생긴
      // 플레이트리스트를 다른 것들과 함께 보는 편이 결과 확인에 낫다.
      navigate('/main-my')
    } catch (error: any) {
      toastUtil.error(
        error.response?.data?.message || '복사하지 못했습니다'
      )
    } finally {
      setIsCopying(false)
    }
  }

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false)
  }

  const handleEditPlateList = () => {
    // 수정은 풀페이지에서 처리. 돌아오면 이 페이지가 재마운트되며 목록을 다시 로드한다.
    setIsBottomSheetOpen(false)
    // 목록에서 가진 커버 URL을 함께 넘겨 편집 화면에서 API 대기 없이 즉시 표시
    navigate(`/platelist/${categoryId}/edit`, {
      state: { thumbnail: categoryThumbnail },
    })
  }

  const handleDeletePlate = async () => {
    if (!categoryId) {
      toastUtil.error('카테고리 정보를 불러오는데 실패했습니다')
      return
    }

    if (isMine) {
      // 소유자인 경우: 플레이트 전체 삭제
      const confirmed = await dialogUtil.confirm({
        message: '플레이트리스트를 삭제하시겠어요?',
        confirmText: '삭제',
        cancelText: '취소',
      })

      if (!confirmed) {
        return
      }

      try {
        await bookmarkApi.deleteBookmarkCategory(categoryId)
        toastUtil.success('플레이트리스트가 삭제되었습니다')
        navigate(-1)
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          '플레이트 삭제 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    } else if (isMember) {
      // 멤버인 경우: 멤버에서 삭제
      const confirmed = await dialogUtil.confirm({
        message: '플레이트리스트에서 나가시겠어요?',
        confirmText: '나가기',
        cancelText: '취소',
      })

      if (!confirmed) {
        return
      }

      try {
        await bookmarkApi.removeMemberFromCategory(categoryId)
        toastUtil.success('플레이트리스트에서 나갔습니다')
        navigate(-1)
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          '플레이트에서 나가는 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const renderCategoryItem = (category: SavedPlaceCategory) => {
    const categoryCode = category.category
    const iconPath = getCategoryIconBrown(categoryCode)
    const categoryName = getCategoryName(categoryCode) || category.name

    return (
      <div
        key={category.category}
        className={`
          w-full
          flex
          items-center
          px-20px
          py-16px
          cursor-pointer
          border-b
          border-472410-20%
          select-none
          [-webkit-touch-callout:none]
          [&_img]:select-none
          [&_img]:[-webkit-touch-callout:none]
        `}
        onClick={() => handleCategoryClick(categoryCode)}
      >
        {/* 아이콘 */}
        {iconPath && (
          <img
            src={iconPath}
            alt={categoryName}
            draggable={false}
            className="w-28px h-28px object-contain"
          />
        )}

        <Gap width={12} />

        {/* 카테고리 이름 */}
        <div className="flex-1 font-pretendard font-bold text-16px leading-125% text-brown-80%-main">
          {categoryName}
        </div>

        <Gap width={8} />

        {/* 개수 */}
        <div className="font-pretendard font-bold text-18px leading-125% text-brown-80%-main">
          {category.count}
        </div>

        <Gap width={8} />

        {/* 화살표 */}
        <img
          src="/images/bold-arrow-right-icon.png"
          alt="이동"
          draggable={false}
          className="w-24px h-24px object-contain"
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <div className="app-container flex-1 flex flex-col overflow-hidden">
          <AppBar title={categoryName || '플레이트 카테고리'} isBottomBorder />
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner size={28} borderWidth={2} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        <AppBar
          title={categoryName || '플레이트 카테고리'}
          isBottomBorder
          rightElement={
            <>
              {/* 소유자이거나 멤버인 경우 메뉴 표시 */}
              {canShowMenu && (
                <button
                  type="button"
                  aria-label="더보기"
                  onClick={handleMeatballClick}
                  /*
                    자세히보기(ReviewDetail)의 더보기 버튼과 **같은 규격**이다.
                    예전에는 28px PNG 아이콘에 px-12px/py-8px 라, 같은 자리에
                    있는 같은 버튼인데 크기·색·오른쪽 여백이 전부 달랐다.
                    점을 SVG 로 그려 두 화면이 한 벌로 보이게 맞춘다.
                  */
                  className="h-48px flex items-center justify-end pr-20px hover:opacity-70 text-[#6B5E46]"
                >
                  {/* 가로로 놓인 점 세 개 */}
                  <svg width="18" height="4" viewBox="0 0 18 4" fill="none">
                    <circle cx="1.8" cy="2" r="1.8" fill="currentColor" />
                    <circle cx="9" cy="2" r="1.8" fill="currentColor" />
                    <circle cx="16.2" cy="2" r="1.8" fill="currentColor" />
                  </svg>
                </button>
              )}
            </>
          }
        />

        {/* 카테고리 목록 또는 비공개 메시지 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {isPrivate ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none">
              <img
                src="/images/lock-icon.png"
                alt="비공개"
                draggable={false}
                className={`
                  w-96px
                  h-96px
                  object-contain
                  select-none
                  [-webkit-touch-callout:none]
                `}
              />

              <Gap height={16} />

              <div className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
                비공개 플레이트리스트입니다.
              </div>
            </div>
          ) : (
            categories.map((category) => renderCategoryItem(category))
          )}
        </div>
      </div>

      {/* 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleCloseBottomSheet}
        items={
          isMine
            ? [
                {
                  text: '플레이트리스트 수정',
                  onClick: handleEditPlateList,
                },
                {
                  text: '플레이트리스트 삭제',
                  onClick: handleDeletePlate,
                },
              ]
            : isMember
              ? [
                  {
                    text: '플레이트리스트 수정',
                    onClick: handleEditPlateList,
                  },
                  {
                    text: '플레이트리스트 나가기',
                    onClick: handleDeletePlate,
                  },
                ]
              : // 남의 리스트 — 고칠 권한이 없으므로 복사만 보인다
                [
                  {
                    text: '플레이트리스트 복사',
                    onClick: handleCopyPlateList,
                  },
                ]
        }
      />

    </div>
  )
}

export default PlateCategory
